/**
 * Jewelry customization profiles — locket photos, chain lengths, ring/bracelet sizes, engraving.
 * Cart/order stores compact JSON prefixed with `j:` (see serializeJewelryCustomization).
 */

import { normalizePersonalizationLetter, formatCharCustomizationLabel, productCharPersonalizationEnabled } from '../productPersonalization';
import { productMultiPieceEnabled } from '../pieceCustomization/pieceConfig';
import { BUILTIN_JEWELRY_PROFILES } from './jewelryBuiltinProfiles';
import { getJewelryProfile } from './jewelryProfileRegistry';

export { BUILTIN_JEWELRY_PROFILES } from './jewelryBuiltinProfiles';
export { getJewelryProfile, getJewelryProfileIds, getJewelryProfiles } from './jewelryProfileRegistry';

export const JEWELRY_PREFIX = 'j:';

/** @typedef {{ value: string, label: string, priceDelta?: number }} JewelryOption */

/** @type {JewelryOption[]} */
export const DEFAULT_CHAIN_OPTIONS = [
  { value: '14', label: '14″ choker', priceDelta: 0 },
  { value: '16', label: '16″ standard', priceDelta: 0 },
  { value: '18', label: '18″', priceDelta: 150 },
  { value: '20', label: '20″', priceDelta: 300 },
  { value: '22', label: '22″ long', priceDelta: 450 },
  { value: 'none', label: 'Pendant only (no chain)', priceDelta: -100 },
];

/** @type {JewelryOption[]} */
export const DEFAULT_RING_SIZES = [
  { value: '5', label: 'Size 5 (US)', priceDelta: 0 },
  { value: '6', label: 'Size 6', priceDelta: 0 },
  { value: '7', label: 'Size 7', priceDelta: 0 },
  { value: '8', label: 'Size 8', priceDelta: 0 },
  { value: '9', label: 'Size 9', priceDelta: 0 },
  { value: 'adjustable', label: 'Adjustable / free size', priceDelta: 0 },
];

/** @type {JewelryOption[]} */
export const DEFAULT_BRACELET_SIZES = [
  { value: '6', label: '6″ (petite)', priceDelta: 0 },
  { value: '7', label: '7″ (standard)', priceDelta: 0 },
  { value: '8', label: '8″', priceDelta: 0 },
  { value: 'adjustable', label: 'Adjustable with extender', priceDelta: 0 },
];

/** @type {{ chainOptions: JewelryOption[], ringOptions: JewelryOption[], braceletOptions: JewelryOption[] }} */
let storeJewelryOptionDefaults = {
  chainOptions: DEFAULT_CHAIN_OPTIONS,
  ringOptions: DEFAULT_RING_SIZES,
  braceletOptions: DEFAULT_BRACELET_SIZES,
};

/** @param {{ chainOptions?: JewelryOption[], ringOptions?: JewelryOption[], braceletOptions?: JewelryOption[] } | null | undefined} defaults */
export function setJewelryStoreOptionDefaults(defaults) {
  storeJewelryOptionDefaults = {
    chainOptions:
      Array.isArray(defaults?.chainOptions) && defaults.chainOptions.length
        ? defaults.chainOptions
        : DEFAULT_CHAIN_OPTIONS,
    ringOptions:
      Array.isArray(defaults?.ringOptions) && defaults.ringOptions.length
        ? defaults.ringOptions
        : DEFAULT_RING_SIZES,
    braceletOptions:
      Array.isArray(defaults?.braceletOptions) && defaults.braceletOptions.length
        ? defaults.braceletOptions
        : DEFAULT_BRACELET_SIZES,
  };
}

export function getJewelryStoreOptionDefaults() {
  return storeJewelryOptionDefaults;
}

/** @param {'chain'|'ring'|'bracelet'} key */
export function getStoreJewelryOptions(key) {
  if (key === 'chain') return storeJewelryOptionDefaults.chainOptions;
  if (key === 'ring') return storeJewelryOptionDefaults.ringOptions;
  if (key === 'bracelet') return storeJewelryOptionDefaults.braceletOptions;
  return [];
}

/** Hard-coded fallbacks before any admin store defaults are loaded. */
export function getBuiltinJewelryOptions(key) {
  if (key === 'chain') return DEFAULT_CHAIN_OPTIONS;
  if (key === 'ring') return DEFAULT_RING_SIZES;
  if (key === 'bracelet') return DEFAULT_BRACELET_SIZES;
  return [];
}

/** @deprecated use getJewelryProfiles() — built-in only */
export const JEWELRY_PROFILES = BUILTIN_JEWELRY_PROFILES;

/** @param {string} profileId */
export function getProfileFieldDefs(profileId) {
  return getJewelryProfile(profileId)?.fields || [];
}

/** Core + optional fields turned on by default when admin has not configured yet. */
export function getDefaultEnabledFieldsForProfile(profileId) {
  return getProfileFieldDefs(profileId)
    .filter((f) => !f.optional || f.defaultEnabled)
    .map((f) => f.id);
}

/** @param {object} product */
export function getEnabledFieldIds(product) {
  const opts = normalizeProductJewelryOptions(product);
  return Array.isArray(opts.enabledFields) ? opts.enabledFields : [];
}

/** @param {object} field @param {object} product */
export function isJewelryFieldEnabled(field, product) {
  return getEnabledFieldIds(product).includes(field.id);
}

/** @param {object} field @param {object} product */
export function isJewelryFieldRequired(field, product) {
  const opts = getProductJewelryOptions(product);
  if (opts.requiredFields && field.id in opts.requiredFields) {
    return Boolean(opts.requiredFields[field.id]);
  }
  if (field.optional) return false;
  return Boolean(field.required);
}

/** @param {string} profileId @param {object} [existing] */
export function defaultCustomizationOptionsForProfile(profileId, existing = {}) {
  const base = existing && typeof existing === 'object' && !Array.isArray(existing) ? { ...existing } : {};
  return normalizeProductJewelryOptions({
    customization_profile: profileId,
    customization_options: {
      ...base,
      enabledFields: getDefaultEnabledFieldsForProfile(profileId),
      requiredFields: base.requiredFields && typeof base.requiredFields === 'object' ? base.requiredFields : {},
      configVersion: 2,
    },
  });
}

function resolveOptions(field, productOptions) {
  const key = field.optionsKey;
  if (key === 'chain') {
    return productOptions?.chainOptions?.length ? productOptions.chainOptions : getStoreJewelryOptions('chain');
  }
  if (key === 'ring') {
    return productOptions?.ringOptions?.length ? productOptions.ringOptions : getStoreJewelryOptions('ring');
  }
  if (key === 'bracelet') {
    return productOptions?.braceletOptions?.length ? productOptions.braceletOptions : getStoreJewelryOptions('bracelet');
  }
  return field.options || [];
}

/** @param {object} product */
export function getProductJewelryProfileId(product) {
  const id = String(product?.customization_profile || '').trim().toLowerCase();
  if (id && getJewelryProfile(id)) return id;
  return '';
}

/** @param {object} product */
export function productJewelryCustomizationEnabled(product) {
  return Boolean(getProductJewelryProfileId(product));
}

/** @param {object} product */
export function getProductJewelryOptions(product) {
  return normalizeProductJewelryOptions(product);
}

/**
 * Normalize API/DB customization_options (may be [], null, or legacy all-fields list).
 * @param {object} product
 */
export function normalizeProductJewelryOptions(product) {
  const profileId = getProductJewelryProfileId(product);
  if (!profileId) return {};

  const raw = product?.customization_options;
  let opts = {};
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    opts = { ...raw };
  } else if (typeof raw === 'string' && raw.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        opts = { ...parsed };
      }
    } catch {
      opts = {};
    }
  }

  const fieldDefs = getProfileFieldDefs(profileId);
  const valid = new Set(fieldDefs.map((f) => f.id));
  const coreIds = fieldDefs.filter((f) => !f.optional).map((f) => f.id);
  const optionalIds = fieldDefs.filter((f) => f.optional).map((f) => f.id);
  const allIds = fieldDefs.map((f) => f.id);

  let enabled = Array.isArray(opts.enabledFields)
    ? opts.enabledFields.filter((id) => valid.has(id))
    : [];

  // Missing/empty enabledFields → profile defaults (e.g. locket = photo only).
  if (enabled.length === 0) {
    enabled = getDefaultEnabledFieldsForProfile(profileId);
  } else if (
    optionalIds.length > 0 &&
    enabled.length === allIds.length &&
    optionalIds.every((id) => enabled.includes(id)) &&
    coreIds.every((id) => enabled.includes(id)) &&
    !opts.configVersion
  ) {
    // Legacy save before per-product toggles — treat as unset optional fields.
    enabled = getDefaultEnabledFieldsForProfile(profileId);
  }

  const requiredFields =
    opts.requiredFields && typeof opts.requiredFields === 'object' && !Array.isArray(opts.requiredFields)
      ? { ...opts.requiredFields }
      : {};

  for (const key of ['chainOptions', 'ringOptions', 'braceletOptions']) {
    if (Array.isArray(opts[key])) {
      const hasDraft = opts[key].some((row) => !String(row?.label ?? '').trim());
      if (!hasDraft) {
        const cleaned = sanitizeJewelryOptions(opts[key]);
        if (cleaned.length) opts[key] = cleaned;
        else delete opts[key];
      }
    }
  }

  return {
    ...opts,
    configVersion: 2,
    enabledFields: enabled,
    requiredFields,
  };
}

/** @param {string} label @param {number} index */
export function suggestJewelryOptionValue(label, index = 0) {
  const trimmed = String(label || '').trim();
  if (!trimmed) return `option_${index + 1}`;
  const lower = trimmed.toLowerCase();
  if (lower.includes('no chain') || lower.includes('pendant only')) return 'none';
  if (lower.includes('adjustable')) return 'adjustable';
  const num = trimmed.match(/\d+/);
  if (num) return num[0];
  return (
    trimmed
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 32) || `option_${index + 1}`
  );
}

/** @param {JewelryOption} row @param {number} index */
export function normalizeJewelryOptionRow(row, index = 0) {
  const label = String(row?.label ?? '').trim();
  let value = String(row?.value ?? '').trim();
  if (!value && label) value = suggestJewelryOptionValue(label, index);
  if (!value) value = `option_${index + 1}`;
  return {
    value,
    label,
    priceDelta: Number(row?.priceDelta) || 0,
  };
}

/** @param {JewelryOption[] | null | undefined} options */
export function sanitizeJewelryOptions(options) {
  return (options || [])
    .map((row, index) => normalizeJewelryOptionRow(row, index))
    .filter((row) => row.label);
}

/** @param {JewelryOption[] | null | undefined} options */
export function jewelryOptionsToLines(options) {
  return (options || [])
    .map((o) => {
      const delta = Number(o.priceDelta) || 0;
      return `${o.value}|${o.label}|${delta}`;
    })
    .join('\n');
}

/**
 * @param {string} text
 * @param {{ optionKey?: string }} [opts]
 */
export function jewelryLinesToOptions(text, { optionKey = '' } = {}) {
  const lines = String(text || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return null;
  const out = [];
  for (const line of lines) {
    const parts = line.split('|').map((p) => p.trim());
    if (parts.length < 2 || !parts[0]) continue;
    const entry = { value: parts[0], label: parts[1] };
    if (parts.length >= 3 && parts[2] !== '') {
      entry.priceDelta = Number(parts[2]) || 0;
    } else if (optionKey === 'chain') {
      entry.priceDelta = 0;
    }
    out.push(entry);
  }
  return out.length ? out : null;
}

/** @param {object} product */
export function getJewelryProfileForProduct(product) {
  const id = getProductJewelryProfileId(product);
  if (!id) return null;
  return getJewelryProfile(id);
}

/** @param {object} product */
export function getJewelryFieldsForProduct(product) {
  const profile = getJewelryProfileForProduct(product);
  if (!profile) return [];
  const opts = getProductJewelryOptions(product);
  const enabled = new Set(getEnabledFieldIds(product));
  return profile.fields
    .filter((f) => enabled.has(f.id))
    .map((f) => ({
      ...f,
      required: isJewelryFieldRequired(f, product),
      options: f.type === 'select' ? resolveOptions(f, opts) : undefined,
    }));
}

/** Product cards: inline dropdowns only — photo/text fields belong on the product page. */
export function jewelryGridInlineEligible(product) {
  if (productMultiPieceEnabled(product)) return false;
  const fields = getJewelryFieldsForProduct(product);
  if (!fields.length) return false;
  return fields.every((f) => f.type === 'select') && fields.length <= 2;
}

/** @param {object} values @param {object} product */
export function validateJewelryCustomization(values, product) {
  const fields = getJewelryFieldsForProduct(product);
  const errors = {};
  for (const field of fields) {
    const val = values?.[field.id];
    if (field.type === 'photo') {
      if (field.required && !String(val || '').trim()) {
        errors[field.id] = 'Please upload a photo.';
      }
      continue;
    }
    if (field.type === 'select') {
      const v = String(val || '').trim();
      if (field.required && !v) {
        errors[field.id] = `Choose ${field.label.toLowerCase()}.`;
      }
      continue;
    }
    const text = String(val || '').trim();
    if (field.required && !text) {
      errors[field.id] = `${field.label} is required.`;
    }
    if (text && field.maxLength && text.length > field.maxLength) {
      errors[field.id] = `Max ${field.maxLength} characters.`;
    }
  }
  return { ok: Object.keys(errors).length === 0, errors };
}

/** @param {object} values @param {object} product */
export function jewelryCustomizationPriceDelta(values, product) {
  const fields = getJewelryFieldsForProduct(product);
  let delta = 0;
  for (const field of fields) {
    if (field.type !== 'select') continue;
    const v = String(values?.[field.id] || '').trim();
    const opt = (field.options || resolveOptions(field, getProductJewelryOptions(product))).find(
      (o) => o.value === v
    );
    if (opt?.priceDelta) delta += Number(opt.priceDelta) || 0;
  }
  return delta;
}

function optionLabel(options, value) {
  const v = String(value || '').trim();
  const hit = (options || []).find((o) => o.value === v);
  return hit?.label || v;
}

/** @param {object} values @param {object} product */
export function formatJewelryCustomizationLabel(values, product) {
  const fields = getJewelryFieldsForProduct(product);
  const parts = [];
  for (const field of fields) {
    const val = values?.[field.id];
    if (!val) continue;
    if (field.type === 'photo') {
      parts.push('Photo uploaded');
      continue;
    }
    if (field.type === 'select') {
      const opts = field.options || resolveOptions(field, getProductJewelryOptions(product));
      parts.push(`${field.label}: ${optionLabel(opts, val)}`);
      continue;
    }
    parts.push(`${field.label}: ${String(val).trim()}`);
  }
  return parts.join(' · ');
}

/** @param {object} values @param {object} product */
export function serializeJewelryCustomization(values, product) {
  const profileId = getProductJewelryProfileId(product);
  const fields = getJewelryFieldsForProduct(product);
  const productOpts = getProductJewelryOptions(product);
  const display = {};
  for (const field of fields) {
    const val = values?.[field.id];
    if (val == null || val === '') continue;
    if (field.type === 'photo') {
      display[field.id] = 'Photo uploaded';
      continue;
    }
    if (field.type === 'select') {
      const opts = field.options || resolveOptions(field, productOpts);
      display[field.id] = optionLabel(opts, val);
      continue;
    }
    display[field.id] = String(val).trim();
  }
  const payload = {
    v: 1,
    profile: profileId,
    values: { ...values },
    label: formatJewelryCustomizationLabel(values, product),
    display,
  };
  return JEWELRY_PREFIX + JSON.stringify(payload);
}

/** @param {string} raw */
export function parseJewelryCustomization(raw) {
  const s = String(raw || '').trim();
  if (!s.startsWith(JEWELRY_PREFIX)) return null;
  try {
    const data = JSON.parse(s.slice(JEWELRY_PREFIX.length));
    if (!data || typeof data !== 'object') return null;
    return data;
  } catch {
    return null;
  }
}

/** @param {string} raw @param {object} [product] */
export function parseCustomization(raw, product) {
  if (String(raw || '').trim().startsWith('m:')) {
    try {
      const piece = JSON.parse(String(raw).trim().slice(2));
      return {
        type: 'piece',
        data: piece,
        label: piece?.label || 'Multi-piece set',
      };
    } catch {
      return { type: 'piece', data: null, label: 'Multi-piece set' };
    }
  }
  const jewelry = parseJewelryCustomization(raw);
  if (jewelry) {
    return {
      type: 'jewelry',
      data: jewelry,
      values: jewelry.values || {},
      label: jewelry.label || formatJewelryCustomizationLabel(jewelry.values || {}, product),
    };
  }
  const letter = normalizePersonalizationLetter(raw);
  if (letter) {
    return { type: 'letter', letter, label: formatCharCustomizationLabel(letter) };
  }
  const text = String(raw || '').trim();
  if (text) return { type: 'text', text, label: text };
  return { type: 'none', label: '' };
}

/** @param {string} raw @param {object} [product] */
export function formatCustomizationDisplay(raw, product) {
  const p = parseCustomization(raw, product);
  return p.label || '';
}

/**
 * Structured customization for admin orders (photos, chain size, engraving, etc.).
 * @param {string} raw
 * @param {object} [product] Optional product row for per-product option lists (orders without display snapshot).
 * @returns {null | {
 *   profileId: string,
 *   profileLabel: string,
 *   summary: string,
 *   fields: Array<{ id: string, label: string, type: string, value: string, photoUrl?: string }>,
 *   photos: Array<{ url: string, fieldLabel: string }>,
 * }}
 */
export function describeJewelryCustomization(raw, product = null) {
  const jewelry = parseJewelryCustomization(raw);
  if (!jewelry) return null;

  const profile = getJewelryProfile(jewelry.profile);
  const values = jewelry.values && typeof jewelry.values === 'object' ? jewelry.values : {};
  const displayMap =
    jewelry.display && typeof jewelry.display === 'object' && !Array.isArray(jewelry.display)
      ? jewelry.display
      : null;

  const productLike = product || { customization_profile: jewelry.profile };
  const fieldDefs = product
    ? getJewelryFieldsForProduct(productLike)
    : (profile?.fields || []).filter((f) => {
        const val = values[f.id];
        return val != null && val !== '';
      });

  const productOpts = product ? getProductJewelryOptions(productLike) : {};
  const fields = [];
  const photos = [];

  for (const fieldDef of fieldDefs) {
    const val = values[fieldDef.id];
    if (val == null || val === '') continue;

    if (fieldDef.type === 'photo') {
      const url = String(val).trim();
      if (!url) continue;
      fields.push({
        id: fieldDef.id,
        label: fieldDef.label,
        type: 'photo',
        value: displayMap?.[fieldDef.id] || 'Uploaded',
        photoUrl: url,
      });
      photos.push({ url, fieldLabel: fieldDef.label });
      continue;
    }

    if (fieldDef.type === 'select') {
      const opts = fieldDef.options || resolveOptions(fieldDef, productOpts);
      fields.push({
        id: fieldDef.id,
        label: fieldDef.label,
        type: 'select',
        value: displayMap?.[fieldDef.id] || optionLabel(opts, val),
      });
      continue;
    }

    fields.push({
      id: fieldDef.id,
      label: fieldDef.label,
      type: fieldDef.type || 'text',
      value: displayMap?.[fieldDef.id] || String(val).trim(),
    });
  }

  const summaryProduct = product || { customization_profile: jewelry.profile };
  return {
    profileId: jewelry.profile,
    profileLabel: profile?.label || jewelry.profile || 'Custom jewelry',
    summary:
      jewelry.label ||
      formatJewelryCustomizationLabel(values, summaryProduct),
    fields,
    photos,
  };
}

/** Plain-text line for packing slips, emails, and exports. */
export function formatOrderItemCustomizationPlain(raw) {
  if (String(raw || '').trim().startsWith('m:')) {
    const { formatOrderItemPieceCustomizationPlain } = require('../pieceCustomization/pieceCustomization');
    return formatOrderItemPieceCustomizationPlain(raw);
  }

  const jewelry = describeJewelryCustomization(raw);
  if (jewelry) {
    const parts = jewelry.fields.map((f) => {
      if (f.type === 'photo') return `${f.label}: customer photo attached`;
      return `${f.label}: ${f.value}`;
    });
    return parts.join(' · ') || jewelry.summary || 'Customized';
  }

  const letter = normalizePersonalizationLetter(raw);
  if (letter) return formatCharCustomizationLabel(letter);

  const text = String(raw || '').trim();
  return text;
}

/** @param {string} raw */
export function orderItemHasCustomization(raw) {
  return Boolean(String(raw || '').trim());
}

/** @param {Array<{ customization_text?: string }>} items */
export function orderHasWorkshopPhotos(items) {
  return (items || []).some((it) => {
    const d = describeJewelryCustomization(it.customization_text);
    return Boolean(d?.photos?.length);
  });
}

/** @param {string} raw @param {object} product */
export function customizationUnitPriceDelta(raw, product) {
  if (String(raw || '').trim().startsWith('m:')) {
    const { parsePieceCustomization, pieceCustomizationPriceDelta } = require('../pieceCustomization/pieceCustomization');
    const piece = parsePieceCustomization(raw);
    if (piece?.mode === 'jewelry') {
      const values = (piece.pieces || []).map((p) => p.values || {});
      return pieceCustomizationPriceDelta(values, product);
    }
    return 0;
  }
  const p = parseCustomization(raw, product);
  if (p.type === 'jewelry') {
    return jewelryCustomizationPriceDelta(p.values, product);
  }
  return 0;
}

/** Whether product needs any pre-cart customization (jewelry, letter, or multi-piece set). */
export function productNeedsPreCartCustomization(product) {
  if (
    productMultiPieceEnabled(product) &&
    (productJewelryCustomizationEnabled(product) || productCharPersonalizationEnabled(product))
  ) {
    return true;
  }
  if (productJewelryCustomizationEnabled(product)) return true;
  return productCharPersonalizationEnabled(product);
}
